import React from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { NextRecurringSession } from '@uplift/api';
import { useTimezone } from '@uplift/contexts';
import { CalendarDateService } from '@uplift/services';
import { Button } from '../../../../Button';
import { useL10n } from '@uplift/hooks';
import { useRecurringListQuery } from '@uplift/queries';
import { Loader } from '../../../../Loader';
import { CancelSessionBlock } from './CancelSessionBlock.component';
import { InCallUpcomingAppointmentSingleItem } from './InCallUpcomingAppointmentSingleItem.component';


type RecurringListProps = {
  seriesAppointment: NextRecurringSession;
  nextSessionDate?: string;
  excludeAppointmentId?: string;
  cancellable?: boolean;
};

const RecurringList: React.VFC<RecurringListProps> = ({ seriesAppointment, nextSessionDate, excludeAppointmentId, cancellable }) => {
  const { data = [] } = useRecurringListQuery(seriesAppointment.appointmentSeriesId, 4, { suspense: true });

  const appointments = React.useMemo(() => {
    const filtered = data?.filter(a => a.id !== excludeAppointmentId) ?? [];

    return filtered.slice(0, 3);
  }, [ data, excludeAppointmentId ]);

  return (
    <>
      {appointments?.map(s => {
        return (
          <InCallUpcomingAppointmentSingleItem
            key={s.id}
            appointment={s}
            isNext={nextSessionDate === s.fromDatetime}
            cancellable={cancellable}
          />
        );
      })}
    </>
  );
};


const useStyles = makeStyles({ name: 'InCallUpcomingAppointmentRecurringItem' })(theme => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,

    '&:last-child': {
      borderBottom: 'none',
    }
  },
  header: {
    display: 'flex',
    alignItems: 'center',
  },

  details: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: theme.spacing(0.5, 0),
  },

  items: {
    paddingLeft: theme.spacing(2),
  }
}));


export type InCallUpcomingAppointmentRecurringItemProps = {
  appointment: NextRecurringSession;
  isNext: boolean;
  excludeAppointmentId?: string;
  cancellable?: boolean;
};


export const InCallUpcomingAppointmentRecurringItem: React.VFC<InCallUpcomingAppointmentRecurringItemProps> = ({ appointment, isNext, excludeAppointmentId, cancellable }) => {
  const [ cancelling, setCancelling ] = React.useState(false);
  const l10n = useL10n();
  const timezone = useTimezone();
  const { classes } = useStyles();

  const recurringTitle = React.useMemo(() => {
    const weekDay = CalendarDateService.format(appointment.fromDatetime, 'dddd', undefined, { timezone: timezone });

    return l10n.dashboard.appointmentRecurring[appointment.recurring]?.(weekDay);
  }, [ l10n, appointment, timezone ]);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.details}>
          <Typography>{recurringTitle}</Typography>
        </Box>
        {!cancelling && cancellable && (
          <Box>
            <Button size="small" variant="outlined" onClick={() => setCancelling(true)}>CANCEL SERIES</Button>
          </Box>
        )}
      </Box>
      {cancellable && (
        <Collapse in={cancelling}>
          <CancelSessionBlock
            shown={cancelling}
            appointment={appointment}
            mode="all"
            onClose={() => setCancelling(false)}
          />
        </Collapse>
      )}
      <React.Suspense fallback={<Loader height="120px" color="secondary" />}>
        <Box className={classes.items}>
          <RecurringList
            seriesAppointment={appointment}
            excludeAppointmentId={excludeAppointmentId}
            nextSessionDate={isNext ? appointment.fromDatetime : undefined}
            cancellable={cancellable}
          />
        </Box>
      </React.Suspense>
    </Box>
  );
};
